import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { addressRepository } from '@entities/address'
import {
  createBaseSelector,
  registerSlice,
  StatusType
} from '@shared/lib/redux'

import { RectBoundsCoordsType } from './config'
import { MapBoundsType, MapConfigType } from './types'

export type MapStore = {
  common: MapConfigType
  mapBounds: MapBoundsType
  isDrawingMode: boolean
  status: StatusType
  error: string | null
  clickCoords: { latitude: number; longitude: number }
}

const radiusInDegrees = 0.1
const defaultCity = addressRepository.getCity()
const latitude = defaultCity?.getLatitude()
const longitude = defaultCity?.getLongitude()

const hasLatitude = defaultCity && latitude
const hasLongitude = defaultCity && longitude

const initialState: MapStore = {
  common: {
    latitude: hasLatitude ? latitude : 0,
    longitude: hasLongitude ? longitude : 0,
    zoom: 13
  },
  mapBounds: {
    minLat: hasLatitude ? latitude - radiusInDegrees : 0,
    minLon: hasLongitude ? longitude - radiusInDegrees : 0,
    maxLat: hasLatitude ? latitude + radiusInDegrees : 0,
    maxLon: hasLongitude ? longitude + radiusInDegrees : 0
  },
  isDrawingMode: false,
  status: 'idle',
  error: null,
  clickCoords: {
    latitude: hasLatitude ? latitude : 0,
    longitude: hasLongitude ? longitude : 0
  }
}

export const mapSlice = createSlice({
  name: 'mapSlice',
  initialState,
  reducers: {
    mapClick: (state, _action: PayloadAction<MapConfigType>) => {
      return state
    },
    setMapZoom: (state, action: PayloadAction<number>) => {
      state.common.zoom = action.payload
    },
    setMapViewBounds: (state, action: PayloadAction<RectBoundsCoordsType>) => {
      state.mapBounds.minLat = action.payload.minPoint.lat
      state.mapBounds.minLon = action.payload.minPoint.long
      state.mapBounds.maxLat = action.payload.maxPoint.lat
      state.mapBounds.maxLon = action.payload.maxPoint.long
    },
    toggleDrawingMode: (state, action: PayloadAction<boolean>) => {
      state.isDrawingMode = action.payload
    },
    reset: (_state) => {
      return initialState
    }
  }
})

const mapBaseSelector = createBaseSelector(mapSlice)
registerSlice([mapSlice])

const selectMapCommon = createSelector(mapBaseSelector, (s) => {
  return s.common
})
const selectMapBounds = createSelector(mapBaseSelector, (s) => {
  return s.mapBounds
})
const selectDrawingMode = createSelector(mapBaseSelector, (s) => {
  return s.isDrawingMode
})

export const mapStore = {
  actions: { ...mapSlice.actions },
  selectors: {
    selectMapCommon,
    selectMapBounds,
    selectDrawingMode
  },
  name: mapSlice.name
}
